import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCitiesForCountry, detectVisitorCountry } from '../utils/geoCities';
const NAMES = ['María', 'Laura', 'Sofía', 'Camila', 'Valentina', 'Martina', 'Lucía', 'Florencia', 'Paula', 'Daniela', 'Julieta', 'Agustina', 'Micaela', 'Carolina', 'Victoria', 'Rocío', 'Josefina', 'Delfina', 'Catalina', 'Milagros', 'Ana', 'Andrea', 'Natalia', 'Mariana', 'Romina', 'Bárbara', 'Guillermina', 'Sol', 'Macarena', 'Lourdes'];
const DEFAULT_THUMB = 'https://cdn.magicpatterns.com/uploads/wkzGFuHWyP7z23EbiJvcUP/heylloyd-image-1772533088855.png';
interface ToastData {
  name: string;
  city: string;
  product: string;
  priceText: string;
  id: number;
}
function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickRandomPurchase(): {
  product: string;
  price: string;
} {
  return {
    product: 'NutriPeques',
    price: '$15.90 USD'
  };
}
function pickFromOverrides(overrides: {
  product: string;
  price: string;
  weight: number;
}[]): {
  product: string;
  price: string;
} {
  const rand = Math.random();
  let cumulative = 0;
  for (const o of overrides) {
    cumulative += o.weight;
    if (rand < cumulative) return {
      product: o.product,
      price: o.price
    };
  }
  return {
    product: overrides[overrides.length - 1].product,
    price: overrides[overrides.length - 1].price
  };
}
function useReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return prefersReduced;
}
interface PurchaseToastProps {
  priceOverrides?: {
    product: string;
    price: string;
    weight: number;
  }[];
}
export function RecentPurchaseToast({
  priceOverrides
}: PurchaseToastProps = {}) {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [geoReady, setGeoReady] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const recentNamesRef = useRef<string[]>([]);
  const toastIdCounter = useRef(0);
  const citiesRef = useRef<string[]>(getCitiesForCountry(null));
  const shouldReduceMotion = useReducedMotion();
  // Resolve the visitor's country from their IP so the cities shown are local
  useEffect(() => {
    let cancelled = false;
    detectVisitorCountry().then(country => {
      if (cancelled) return;
      citiesRef.current = getCitiesForCountry(country);
      setGeoReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // Hold the notifications back until the visitor has read at least half the
  // page, or has reached the offer section — whichever happens first.
  useEffect(() => {
    if (unlocked) return;
    const unlock = () => setUnlocked(true);
    const checkScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (window.scrollY / scrollable >= 0.5) unlock();
    };
    window.addEventListener('scroll', checkScroll, {
      passive: true
    });
    checkScroll();
    let observer: IntersectionObserver | undefined;
    const offerSection = document.getElementById('oferta');
    if (offerSection && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) unlock();
      }, {
        rootMargin: '0px 0px -25% 0px'
      });
      observer.observe(offerSection);
    }
    return () => {
      window.removeEventListener('scroll', checkScroll);
      observer?.disconnect();
    };
  }, [unlocked]);
  // Start the toast cycle only once geo resolved AND the scroll gate opened
  useEffect(() => {
    if (!geoReady || !unlocked) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    const showNextToast = () => {
      // Pick name (avoid last 10)
      let availableNames = NAMES.filter(n => !recentNamesRef.current.includes(n));
      if (availableNames.length === 0) availableNames = NAMES;
      const selectedName = getRandomItem(availableNames);
      recentNamesRef.current = [selectedName, ...recentNamesRef.current].slice(0, 10);
      // Pick city from geo-detected list
      const selectedCity = getRandomItem(citiesRef.current);
      // Pick product + price
      const purchase = priceOverrides ? pickFromOverrides(priceOverrides) : pickRandomPurchase();
      toastIdCounter.current += 1;
      setToast({
        name: selectedName,
        city: selectedCity,
        product: purchase.product,
        priceText: purchase.price,
        id: toastIdCounter.current
      });
      // Hide after 4s, then schedule next after 8–15s
      timeoutId = setTimeout(() => {
        setToast(null);
        timeoutId = setTimeout(showNextToast, getRandomInt(8000, 15000));
      }, 4000);
    };
    // Short delay after the gate opens so the toast doesn't jump the visitor
    timeoutId = setTimeout(showNextToast, 4000);
    return () => clearTimeout(timeoutId);
  }, [geoReady, unlocked]);
  const animDuration = shouldReduceMotion ? 0 : 0.4;
  return <AnimatePresence data-id="element-495" data-mp="15jniejnd9h9m">
      {toast && <motion.div key={toast.id} initial={{
      opacity: 0,
      scale: shouldReduceMotion ? 1 : 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: shouldReduceMotion ? 1 : 0.9
    }} transition={{
      duration: animDuration,
      ease: [0.16, 1, 0.3, 1]
    }} className="fixed bottom-6 left-0 right-0 mx-auto z-50 pointer-events-none px-4 w-full max-w-sm" data-id="element-496" data-mp="15kf14tvv7kih">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 p-3 flex items-center gap-3 w-full max-w-sm" data-id="element-497" data-mp="3gd017s51yd4e">
            {/* Thumbnail */}
            <div className="shrink-0 relative" data-id="element-498" data-mp="1p6vae7w0w7e3">
              <img src={DEFAULT_THUMB} alt="NutriPeques" className="w-12 h-12 rounded-xl object-cover shadow-sm border border-gray-50" data-id="element-499" data-mp="1p9r7xv3yku20" />
              <div className="absolute -bottom-1 -right-1 bg-[#4CAF50] w-4 h-4 rounded-full border-2 border-white flex items-center justify-center" data-id="element-500" data-mp="1y8ur0z34l9gi">
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-501" data-mp="237621d55pq0b">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-502" data-mp="2cx7ltsqh8m9f" />
                </svg>
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0" data-id="element-503" data-mp="2h0x9mfzbuzte">
              <p className="text-sm text-gray-800 leading-tight" data-id="element-504" data-mp="2lyfik6dchf1e">
                <span className="font-bold" data-id="element-505" data-mp="2lw2urckwoagf">{toast.name}</span> de {toast.city}{' '}
                acaba de comprar
              </p>
              <p className="text-xs text-gray-500 mt-0.5 truncate" data-id="element-506" data-mp="3117alh4ldiql">
                {toast.product}{' '}
                <span className="font-bold text-[#2E7D32]" data-id="element-507" data-mp="2w1ktuxpk1kd1">
                  — {toast.priceText}
                </span>
              </p>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}